import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, Package, ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useShop } from '../contexts/ShopContext';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrderSuccess = () => {
  const [searchParams] = useSearchParams();
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { clearCart } = useShop();

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/shop/checkout/status/${sessionId}`);
      setOrderDetails(response.data);
      // Only clear the cart after payment is CONFIRMED paid.
      // If the customer cancelled/returned, cart stays intact so they can retry.
      if (response.data?.payment_status === 'paid') {
        clearCart();
      }
    } catch (error) {
      console.error('Failed to check payment status:', error);
      setError('Could not verify payment status');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen py-16 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#D9F99D] animate-spin mx-auto mb-4" />
          <p className="text-zinc-400">Confirming your order...</p>
        </div>
      </div>
    );
  }

  if (error || !sessionId) {
    return (
      <div className="min-h-screen py-16">
        <div className="max-w-lg mx-auto px-4 text-center">
          <div className="glass-card rounded-2xl p-8">
            <Package className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">Order Status Unknown</h1>
            <p className="text-zinc-400 mb-6">
              {error || 'We couldn\'t verify your order status. Please check your email for confirmation.'}
            </p>
            <Link to="/shop">
              <Button className="bg-[#D9F99D] text-black hover:bg-[#BEF264]">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Shop
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const isPaid = orderDetails?.payment_status === 'paid';

  return (
    <div className="min-h-screen py-16">
      <div className="max-w-lg mx-auto px-4">
        <div className="glass-card rounded-2xl p-8 text-center">
          {isPaid ? (
            <>
              {/* Success Animation */}
              <div className="relative w-24 h-24 mx-auto mb-6">
                <div className="absolute inset-0 bg-green-500/20 rounded-full animate-ping" />
                <div className="relative w-24 h-24 bg-green-500/20 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-12 h-12 text-green-400" />
                </div>
              </div>

              <h1 className="text-3xl font-bold mb-2">Order Confirmed!</h1>
              <p className="text-zinc-400 mb-6">
                Thank you for your purchase. A confirmation email has been sent to you.
              </p>

              {/* Order Summary */}
              <div className="bg-white/5 rounded-xl p-4 mb-6 text-left">
                <div className="flex justify-between mb-2">
                  <span className="text-zinc-400">Payment Status</span>
                  <span className="text-green-400 font-medium">Paid</span>
                </div>
                {orderDetails?.amount_total && (
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Amount</span>
                    <span className="font-bold text-[#D9F99D]">
                      ${(orderDetails.amount_total / 100).toFixed(2)} {orderDetails.currency?.toUpperCase()}
                    </span>
                  </div>
                )}
              </div>

              {/* Next Steps */}
              <div className="text-left mb-8">
                <h3 className="font-bold mb-3">What's Next?</h3>
                <ul className="space-y-2 text-sm text-zinc-400">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    You'll receive an email confirmation shortly
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    We'll notify you when your order ships
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    Expect delivery within 3-5 business days
                  </li>
                </ul>
              </div>
            </>
          ) : (
            <>
              <Package className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
              <h1 className="text-2xl font-bold mb-2">Payment Processing</h1>
              <p className="text-zinc-400 mb-6">
                Your payment is being processed. Please check your email for confirmation.
              </p>
            </>
          )}

          <div className="flex gap-4">
            <Link to="/shop" className="flex-1">
              <Button className="w-full bg-[#D9F99D] text-black hover:bg-[#BEF264]" data-testid="continue-shopping-btn">
                Continue Shopping
              </Button>
            </Link>
            <Link to="/" className="flex-1">
              <Button variant="outline" className="w-full bg-white/5 border-white/10">
                Back to Home
              </Button>
            </Link>
          </div>
        </div>

        {/* Support Info */}
        <p className="text-center text-zinc-500 text-sm mt-6">
          Questions about your order? Contact us at{' '}
          <a href="mailto:djshadowbookings@gmail.com" className="text-[#D9F99D] hover:underline">
            djshadowbookings@gmail.com
          </a>
        </p>
      </div>
    </div>
  );
};

export default OrderSuccess;
